import {
  Button,
  FormControl,
  Input,
  Stack,
  Text,
  Select,
  useToast,
  Box,
  Heading,
  Flex,
  Image,
  Divider,
  Icon,
  useColorModeValue,
  useColorMode,
  useBreakpointValue,
  Center,
  Checkbox,
  FormLabel,
  Link,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import { FiTrash2 } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { useButtonProps, useCreditCardSecurityToken, useUserProfile } from "hooks";
import { useCopyValue } from "hooks/useCopies";
import { track } from "api/analytics";
import { DEAL_FINDER_PAYMENT_SUBMITTED, DEAL_FINDER_PAYMENT_CANCELLED } from "api/analytics/events";
import { COUNTRY_CODES } from "api/subscription/data/countryCodes";
import { COUNTRY_SUB_DIVISIONS } from "api/subscription/data/countrySubdivisions";
import deal_placeholder from "screens/common/static/images/deal_placeholder_banner.svg";
import deal_placeholder_dark from "screens/common/static/images/deal_placeholder_banner_dark.svg";
import type { DealFinderTicker } from "api/tickers/models/DealFinderTicker";
import { getUserPayments } from "api/billingPlan";
import type { UserPayment } from "types/billing/UserPaymentsResponse";
import { TypingIndicator } from "screens/thread/components/cells/components";
import visaIcon from "screens/common/static/logos/visa-logo.png";
import mastercardIcon from "screens/common/static/logos/mastercard-logo.png";
import americanExpressIcon from "screens/common/static/logos/american-express-logo.png";

export const FormErrorLabel = ({ error, textAlign = "end" }: { error: string; textAlign?: "end" | "start" }) => {
  return (
    <Text width="100%" position={"absolute"} textAlign={textAlign} pt="2px" color="red.500" fontSize={"10px"}>
      {error}
    </Text>
  );
};

interface PurchaseItem {
  id: string;
  name: string;
  company: string;
  exchange: string;
  format: string;
  price: string;
  companyLogoUrl?: string;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (fields: any) => void;
  onClose: () => void;
  deal?: DealFinderTicker;
  selectedProductKey?: string;
  productName?: string;
  productPrice?: string;
}

export const PurchasingCartForm = (props: Props) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<{
    firstName: string;
    lastName: string;
    organization: string;
    address2: string;
    address: string;
    city: string;
    country: string;
    state: string;
    postcode: string;
    acceptTerms: boolean;
  }>();
  const ctaButtonProps = useButtonProps("md", "cta");
  const cancelButtonStyle = useButtonProps("md", "secondary");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { firstName: profileFirstName, lastName: profileLastName, fullName, email, id } = useUserProfile();
  const countryWatch = watch("country", undefined);
  const firstNameInputRef = useRef<HTMLInputElement | null>();
  const { ref: registerFirstNameRef, ...firstNameRegisterProps } = register("firstName", { required: true });
  const chargify = useRef(new window.Chargify());
  const chargifyForm = useRef<HTMLFormElement>(null);
  const toast = useToast();
  const securityToken = useCreditCardSecurityToken();
  const { colorMode } = useColorMode();
  const borderColor = useColorModeValue("#E2E8F0", "#2D3748");
  const bgColor = useColorModeValue("gray.50", "gray.800");
  const purchaseBgColor = useColorModeValue("#F8F8F8", "gray.900");
  const inputsBgColor = useColorModeValue("white", "gray.200");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const [payments, setPayments] = useState<UserPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const purchaseMessage = useCopyValue("copy_deal_finder_purchase_message");

  // Callback function to fetch payments
  const fetchPayments = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const response = await getUserPayments(id);
      setPayments(response);
    } catch (error) {
      setPayments([]);
      console.error("Error fetching payments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Get the latest payment record if available
  const latestPaymentRecord = useMemo(() => {
    if (!payments?.length) return null;
    return payments.find((payment) => payment.isDefault) || payments[0];
  }, [payments]);

  useEffect(() => {
    const formValues = {
      firstName: latestPaymentRecord?.firstName || profileFirstName || "",
      lastName: latestPaymentRecord?.lastName || profileLastName || "",
      address: latestPaymentRecord?.billingAddress || "",
      address2: latestPaymentRecord?.billingAddress2 || "",
      city: latestPaymentRecord?.billingCity || "",
      state: latestPaymentRecord?.billingState || "",
      postcode: latestPaymentRecord?.billingZip || "",
      country: latestPaymentRecord?.billingCountry || "",
      organization: "",
    };

    // Update all form fields at once for better performance
    Object.entries(formValues).forEach(([field, value]) => {
      setValue(field as keyof typeof formValues, value);
    });
  }, [latestPaymentRecord, profileFirstName, profileLastName, setValue]);

  useEffect(() => {
    if (firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    chargify.current.load({
      hideCardImage: true,
      publicKey: window.env.chargifyJsPublicToken,
      type: "card",
      gatewayHandle: window.env.chargifyGatewayHandle,
      serverHost: window.env.maxioSiteHost,
      securityToken: securityToken,
      style: {
        field: {
          backgroundColor: inputsBgColor,
          height: "48px",
          color: textColor,
          fontSize: "0.9rem",
          lineHeight: "0.9rem",
        },
        input: {
          backgroundColor: inputsBgColor,
          height: "48px",
          color: textColor,
          fontSize: "0.9rem",
          lineHeight: "0.9rem",
          "::after": { display: "none" },
        },
      },
      fields: {
        number: {
          id: "card-number",
          required: true,
          selector: "#chargify_form",
          placeholder: "Card Number",
          style: {
            field: {
              minWidth: isMobile ? "11rem" : "24rem",
            },
          },
        },
        month: {
          required: true,
          selector: "#chargify_form",
          style: {
            field: {
              lineHeight: "2rem",
            },
          },
        },
        year: {
          required: true,
          selector: "#chargify_form",
        },
        cvv: {
          required: true,
          selector: "#chargify_form",
        },
      },
    });

    return undefined;
  }, [securityToken, inputsBgColor, textColor, isMobile, isLoading]);

  // Add custom styling to the Chargify iframe after it's loaded
  useLayoutEffect(() => {
    if (isLoading) {
      return;
    }

    const styleChargifyIframe = () => {
      const chargifyFormElement = document.getElementById("chargify_form");
      if (chargifyFormElement) {
        const iframe = chargifyFormElement.querySelector("iframe");
        if (iframe) {
          iframe.style.width = "100%";
          iframe.style.height = "3rem";
          iframe.style.border = "none";
        }
      }
    };

    styleChargifyIframe();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        styleChargifyIframe();
      });
    });

    const chargifyFormElement = document.getElementById("chargify_form");
    if (chargifyFormElement) {
      observer.observe(chargifyFormElement, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
    };
  }, [isLoading]);

  const onConfirm = useCallback(
    async (fields: any) => {
      track(DEAL_FINDER_PAYMENT_SUBMITTED, {
        dealName: props.deal?.name,
        dealExchange: props.deal?.exchange,
        dealTicker: props.deal?.symbol,
        userName: fullName,
        userEmail: email,
      });

      setIsSubmitting(true);

      if (latestPaymentRecord) {
        props.onSubmit(fields);
        return;
      }

      try {
        const token = await new Promise((resolve, reject) => {
          if (!chargifyForm.current) {
            reject(new Error("Chargify form not found"));
            return;
          }

          chargify.current.token(
            chargifyForm.current,
            (token) => {
              resolve(token);
            },
            (error) => {
              reject(new Error("Error fetching token: " + String(error)));
            }
          );
        });

        props.onSubmit({
          ...fields,
          creditCardToken: token,
          chargifyGatewayHandle: window.env.chargifyGatewayHandle,
        });
      } catch (error) {
        toast({
          title: "An error occurred.",
          description: "Unable to process credit card. Please contact support or try again later",
          status: "error",
          duration: 8000,
          isClosable: true,
        });
        setIsSubmitting(false);
      }
    },
    [email, fullName, latestPaymentRecord, props, toast]
  );

  const countryCodeOptions = useMemo(
    () =>
      COUNTRY_CODES.map((country) =>
        Array.isArray(country) ? { label: country[0], value: country[1] } : { label: country, value: country }
      ),
    []
  );

  const countrySubdivisionOptions = useMemo(() => {
    const subdivisions = COUNTRY_SUB_DIVISIONS[countryWatch];
    if (subdivisions) {
      return subdivisions.map(([label, value]) => ({ label, value }));
    }

    return undefined;
  }, [countryWatch]);

  const purchaseItems: PurchaseItem[] = useMemo(() => {
    if (props.deal && props.selectedProductKey && props.productName && props.productPrice) {
      return [
        {
          id: props.selectedProductKey,
          name: props.productName,
          company: props.deal.name,
          exchange: `${props.deal.exchange}: ${props.deal.name.toUpperCase().replace(/\s/g, "")}`,
          format: "PDF",
          price: props.productPrice,
          companyLogoUrl: props.deal.companyLogoUrl || undefined,
        },
      ];
    }

    return [];
  }, [props.deal, props.selectedProductKey, props.productName, props.productPrice]);

  // Calculate totals
  const subtotal = useMemo(() => {
    return purchaseItems.reduce((total, item) => {
      const price = parseFloat(item.price.replace(/[^0-9.]/g, ""));
      return total + price;
    }, 0);
  }, [purchaseItems]);

  const handleOnCancel = useCallback(() => {
    track(DEAL_FINDER_PAYMENT_CANCELLED, {
      dealName: props.deal?.name,
      dealExchange: props.deal?.exchange,
      dealTicker: props.deal?.symbol,
      reportType: props.selectedProductKey,
      userName: fullName,
      userEmail: email,
    });
    props.onClose();
  }, [email, fullName, props]);

  return (
    <Stack
      direction={{ base: "column", md: "row" }}
      spacing={isMobile ? "1rem" : "0"}
      height={isMobile ? "unset" : "100%"}
      width="100%"
      pb={isMobile ? "2rem" : "unset"}>
      <Box
        overflow={"auto"}
        width={{ base: "100%", md: "50%" }}
        p={{ base: "0.5rem", md: "1rem" }}
        borderWidth="1px"
        borderColor={borderColor}
        bg={purchaseBgColor}>
        {latestPaymentRecord && (
          <Stack mb="2rem" mt="1rem">
            <Heading size="md">Your Credit Card</Heading>
            <Text fontSize="sm">{`We will use your credit card ${
              latestPaymentRecord && latestPaymentRecord.maskedCardNumber
                ? `ending in ${latestPaymentRecord && latestPaymentRecord.maskedCardNumber}`
                : "on file"
            } to purchase this report.`}</Text>
          </Stack>
        )}
        {isLoading ? (
          <Center zIndex={100} width="100%" height={`calc(100vh - 15rem)`}>
            <TypingIndicator />
          </Center>
        ) : (
          <>
            <Heading size="md" mb="1rem">
              Billing Information
            </Heading>
            <form onSubmit={handleSubmit(onConfirm)} ref={chargifyForm} noValidate>
              <Stack direction="column" spacing="1rem">
                <Stack spacing="1rem" width="100%">
                  <Stack direction="row" spacing="1rem">
                    <FormControl isInvalid={!!errors.firstName}>
                      <Input
                        bgColor={inputsBgColor}
                        disabled={!!latestPaymentRecord}
                        defaultValue={profileFirstName}
                        size="lg"
                        {...firstNameRegisterProps}
                        id="register-firstName"
                        type="text"
                        placeholder="First Name"
                        borderColor={errors.firstName ? "red.500" : "gray.300"}
                        fontSize="md"
                        boxShadow="none"
                        ref={(e) => {
                          registerFirstNameRef(e);
                          firstNameInputRef.current = e;
                        }}
                        data-chargify="firstName"
                      />
                      {errors.firstName && <FormErrorLabel error="required" />}
                    </FormControl>
                    <FormControl isInvalid={!!errors.lastName}>
                      <Input
                        bgColor={inputsBgColor}
                        disabled={!!latestPaymentRecord}
                        defaultValue={profileLastName}
                        size="lg"
                        {...register("lastName", { required: true })}
                        id="register-lastName"
                        type="text"
                        placeholder="Last Name"
                        borderColor={errors.lastName ? "red.500" : "gray.300"}
                        fontSize="md"
                        boxShadow="none"
                        data-chargify="lastName"
                      />
                      {errors.lastName && <FormErrorLabel error="required" />}
                    </FormControl>
                  </Stack>

                  <FormControl isInvalid={!!errors.organization}>
                    <Input
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      size="lg"
                      id="register-organization"
                      type="string"
                      placeholder="Company Name"
                      {...register("organization", { required: false })}
                      borderColor={errors.organization ? "red.500" : "gray.300"}
                      fontSize="md"
                      boxShadow="none"
                    />
                    {errors.organization && <FormErrorLabel error="required" />}
                  </FormControl>

                  <FormControl isInvalid={!!errors.address}>
                    <Input
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      size="lg"
                      id="register-address"
                      type="string"
                      placeholder="Address"
                      {...register("address", { required: true })}
                      borderColor={errors.address ? "red.500" : "gray.300"}
                      fontSize="md"
                      boxShadow="none"
                      data-chargify="address"
                    />
                    {errors.address && <FormErrorLabel error="required" />}
                  </FormControl>

                  <FormControl>
                    <Input
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      size="lg"
                      {...register("address2", {
                        required: false,
                      })}
                      id="register-address2"
                      type="address2"
                      placeholder="Unit (Optional)"
                      borderColor={errors.address2 ? "red.500" : "gray.300"}
                      fontSize="md"
                      boxShadow="none"
                      data-chargify="address2"
                    />
                  </FormControl>

                  <FormControl isInvalid={!!errors.city}>
                    <Input
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      size="lg"
                      {...register("city", {
                        required: true,
                      })}
                      id="register-city"
                      type="city"
                      placeholder="City"
                      borderColor={errors.city ? "red.500" : "gray.300"}
                      fontSize="md"
                      boxShadow="none"
                      data-chargify="city"
                    />
                    {errors.city && <FormErrorLabel error="required" />}
                  </FormControl>

                  <FormControl isInvalid={!!errors.state}>
                    {countrySubdivisionOptions ? (
                      <Select
                        bgColor={inputsBgColor}
                        disabled={!!latestPaymentRecord}
                        placeholder="State/Province"
                        defaultValue={undefined}
                        size="lg"
                        fontSize={"md"}
                        width={"100%"}
                        value={undefined}
                        {...register("state", { required: true })}
                        data-chargify="state">
                        {countrySubdivisionOptions.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        bgColor={inputsBgColor}
                        disabled={!!latestPaymentRecord}
                        size="lg"
                        id="register-state"
                        type="text"
                        placeholder="State"
                        {...register("state", { required: true })}
                        borderColor={errors.state ? "red.500" : "gray.300"}
                        fontSize="md"
                        boxShadow="none"
                        data-chargify="state"
                      />
                    )}
                    {errors.state && <FormErrorLabel error="required" />}
                  </FormControl>

                  <FormControl isInvalid={!!errors.postcode}>
                    <Input
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      size="lg"
                      id="register-postcode"
                      type="string"
                      placeholder="Zip/Postal Code"
                      {...register("postcode", { required: true })}
                      borderColor={errors.postcode ? "red.500" : "gray.300"}
                      fontSize="md"
                      boxShadow="none"
                      data-chargify="zip"
                    />
                    {errors.postcode && <FormErrorLabel error="required" />}
                  </FormControl>

                  <FormControl isInvalid={!!errors.country}>
                    <Select
                      bgColor={inputsBgColor}
                      disabled={!!latestPaymentRecord}
                      placeholder="Country"
                      size="lg"
                      fontSize="md"
                      width={"100%"}
                      value={undefined}
                      {...register("country", { required: true })}
                      data-chargify="country">
                      {countryCodeOptions.map(({ value, label }, idx) => (
                        <option key={idx} disabled={value === label} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    {errors.country && <FormErrorLabel error="required" />}
                  </FormControl>
                </Stack>

                {!latestPaymentRecord && (
                  <Stack spacing="1rem" width="100%" pt="1rem">
                    <Stack spacing="1rem" width="100%" pt="1rem">
                      <Stack direction="row" spacing="1rem" justifyContent={"space-between"}>
                        <Heading size="md">Credit Card Details</Heading>
                        <Stack direction="row" spacing="3px">
                          <Image src={visaIcon} alt="Visa" height="24px" objectFit="contain" />
                          <Image src={mastercardIcon} alt="Mastercard" height="24px" objectFit="contain" />
                          <Image src={americanExpressIcon} alt="American Express" height="24px" objectFit="contain" />
                        </Stack>
                      </Stack>
                      <FormControl overflow="hidden" height="3rem">
                        <div id="chargify_form" style={{ width: "100%", height: "48px", borderRadius: "md" }} />
                      </FormControl>
                    </Stack>
                    <FormControl isInvalid={!!errors.acceptTerms}>
                      <Stack direction={"row"} spacing="1rem">
                        <Checkbox
                          type="checkbox"
                          {...register("acceptTerms", { required: true })}
                          id="purchase-deal-acceptTerms"
                          borderColor={errors.acceptTerms ? "red.500" : "gray.4700"}
                          fontSize="sm"
                          boxShadow="none"
                        />
                        <FormLabel mb="0" color={textColor} lineHeight="2rem" fontSize="sm" htmlFor="purchase-deal-acceptTerms">
                          Accept{" "}
                          <Link href="https://charliai.com/terms-and-services/" isExternal textDecoration={"underline"}>
                            Terms of Service
                          </Link>
                        </FormLabel>
                      </Stack>
                      {errors.acceptTerms && <FormErrorLabel error="required" textAlign="start" />}
                    </FormControl>
                  </Stack>
                )}
              </Stack>
            </form>
          </>
        )}
      </Box>

      <Box overflow={"auto"} width={{ base: "100%", md: "50%" }} pl={{ base: 0, md: "2rem" }} pt="1rem">
        <Heading size="md" mb="1rem">
          Purchase Details
        </Heading>
        <Stack spacing="2rem" justifyContent={"space-between"} maxH={"calc(100% - 3rem)"} height={"100%"}>
          <Box>
            <Stack spacing="1rem">
              {purchaseItems.map((item) => (
                <Box key={item.id} borderWidth="1px" borderColor={borderColor} borderRadius="md" p="1rem" mb="1rem">
                  <Stack direction="row" justifyContent="space-between">
                    <Stack direction="row" justifyContent="space-between" spacing={"2rem"}>
                      <Box position="relative" overflow="hidden">
                        <Image
                          src={item.companyLogoUrl || undefined}
                          alt={item.name}
                          objectFit="cover"
                          objectPosition="right center"
                          maxWidth="8rem"
                          maxHeight={"3rem"}
                          fallback={
                            <Image
                              src={colorMode === "dark" ? deal_placeholder_dark : deal_placeholder}
                              alt={item.name || "Deal image"}
                              objectFit="cover"
                              objectPosition="right center"
                              width="100%"
                              height="5.5rem"
                            />
                          }
                        />
                      </Box>
                      <Stack spacing="0.5">
                        <Text fontWeight="bold">{item.name}</Text>
                        <Text fontSize="md">{item.company}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {item.exchange}
                        </Text>
                        <Text fontSize="xs">Delivery format: {item.format}</Text>
                      </Stack>
                    </Stack>
                    <Stack alignItems="flex-end">
                      <Text fontWeight="bold">{item.price}</Text>
                      <Icon
                        as={FiTrash2}
                        color="gray.400"
                        cursor="pointer"
                        _hover={{ color: "red.500" }}
                        alignSelf="flex-end"
                        onClick={props.onClose}
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Box mt="2rem">
              <Divider mb="1rem" />
              <Flex justifyContent="space-between" mb="0.5rem">
                <Text>Subtotal:</Text>
                <Text fontWeight="bold">${subtotal.toFixed(2)}</Text>
              </Flex>
              <Flex justifyContent="space-between" mb="0.5rem">
                <Text>Tax:</Text>
                <Text>Incl.</Text>
              </Flex>
              <Divider my="1rem" />
              <Flex justifyContent="space-between" mb="1rem">
                <Text fontWeight="bold">Total:</Text>
                <Text fontWeight="bold" fontSize="lg">
                  ${subtotal.toFixed(2)}
                </Text>
              </Flex>
            </Box>
            <Box mt="2rem" p="1rem" bg={bgColor} borderRadius="md" fontSize="md">
              <Text>{purchaseMessage}</Text>
            </Box>
          </Box>
          <Stack direction="row" spacing="1rem" width="100%" justifyContent="space-between">
            <Button {...cancelButtonStyle} id="purchase-cancel" type="button" onClick={handleOnCancel}>
              Cancel
            </Button>
            <Button {...ctaButtonProps} id="purchase-submit" onClick={handleSubmit(onConfirm)} isLoading={isSubmitting}>
              Pay Now
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
